import {
  type GridPosition,
  type SelectionsMachine,
  type SelectionMode,
} from '@databases/selections';
import { type DatabaseAdapter } from '@databases/selections-api-types';
import { type DatabaseEntry, type DatabaseValue } from '@databases/types/database';

import { type ResolvedSelection } from './types';

export interface SelectionsApiOptions {
  machine: SelectionsMachine;
  adapter: DatabaseAdapter;
}

/**
 * Translates index-based selections from the SelectionsMachine into
 * ID-based database entities using the provided DatabaseAdapter.
 *
 * This bridges the gap between the selection engine (which works with
 * row/column indices) and the database layer (which works with IDs).
 */
export class SelectionsApi {
  #machine: SelectionsMachine;
  #adapter: DatabaseAdapter;

  constructor(options: SelectionsApiOptions) {
    this.#machine = options.machine;
    this.#adapter = options.adapter;
  }

  /**
   * Returns true if any cells, rows, or columns are currently selected.
   * If the mode is idle, this guarantees that nothing is selected.
   */
  hasSelection(): boolean {
    const mode = this.getSelectionMode();

    return mode !== 'idle';
  }

  /**
   * Returns the current selection mode.
   */
  getSelectionMode(): SelectionMode {
    return this.#machine.getState().mode;
  }

  /**
   * Resolves the current selection to database entities.
   * Translates indices to IDs and fetches full field/entry/value data.
   * Returns a discriminated union - narrow on `mode` to access the data.
   */
  getResolvedSelection(): ResolvedSelection {
    const flattened = this.#machine.getFlattenedSelection();

    if (flattened.mode === 'idle') {
      return { mode: 'idle' };
    }

    if (flattened.mode === 'column') {
      return this.#resolveColumnSelection(flattened.values);
    }

    if (flattened.mode === 'row') {
      const rowIndices =
        flattened.values === 'all'
          ? Array.from({ length: this.#adapter.getVisibleEntryIds().length }, (_, i) => i)
          : flattened.values;

      return this.#resolveRowSelection(rowIndices);
    }

    return this.#resolveCellSelection(flattened.values);
  }

  /**
   * Returns true if the selection can move up (row index > 0).
   * Only applies to cell and row modes.
   */
  canMoveUp(): boolean {
    const bounds = this.#getSelectionBounds();

    if (!bounds) {
      return false;
    }

    return bounds.minRow > 0;
  }

  /**
   * Returns true if the selection can move down (row index < last entry).
   * Only applies to cell and row modes.
   */
  canMoveDown(): boolean {
    const bounds = this.#getSelectionBounds();

    if (!bounds) {
      return false;
    }

    const totalEntries = this.#adapter.getVisibleEntryIds().length;

    return bounds.maxRow < totalEntries - 1;
  }

  /**
   * Returns true if the selection can move left (column index > 0).
   * Only applies to cell and column modes.
   */
  canMoveLeft(): boolean {
    const bounds = this.#getSelectionBounds();

    if (!bounds) {
      return false;
    }

    return bounds.minCol > 0;
  }

  /**
   * Returns true if the selection can move right (column index < last field).
   * Only applies to cell and column modes.
   */
  canMoveRight(): boolean {
    const bounds = this.#getSelectionBounds();

    if (!bounds) {
      return false;
    }

    const totalFields = this.#adapter.getVisibleFieldIds().length;

    return bounds.maxCol < totalFields - 1;
  }

  /**
   * Clears all current selections, resets the mode to 'idle',
   * and removes the focus cursor.
   */
  deselectAll() {
    if (this.getSelectionMode() === 'idle') {
      return;
    }

    this.#machine.dispatch({
      type: 'deselectAll',
      source: 'api',
    });
  }

  /**
   * Signals a delete preview intent for the current selection.
   * Effects listen to this for showing UI changes (e.g., turning
   * selection highlights red to indicate a pending delete).
   */
  previewDeleteIntent() {
    this.#machine.dispatch({
      type: 'previewSelectionAction',
      source: 'api',
      intent: 'delete',
    });
  }

  /**
   * Clears any active preview intent, reverting highlights to normal.
   */
  clearPreviewIntent() {
    this.#machine.dispatch({
      type: 'previewSelectionAction',
      source: 'api',
      intent: null,
    });
  }

  /**
   * Translates a single grid position (row/column indices) into database
   * entity IDs, scoped by the active selection mode.
   *
   * | Mode     | `entryId`           | `fieldId`           |
   * |----------|---------------------|---------------------|
   * | `cell`   | row index → ID      | col index → ID      |
   * | `row`    | row index → ID      | `undefined`         |
   * | `column` | `undefined`         | col index → ID      |
   *
   * Either value may be `undefined` if the index falls outside the
   * adapter's visible range (e.g. a stale position after a view change).
   */
  resolvePositionToIds(
    position: GridPosition,
    mode: Exclude<SelectionMode, 'idle'>
  ): { entryId: string | undefined; fieldId: string | undefined } {
    switch (mode) {
      case 'cell':
        return {
          entryId: this.#adapter.getVisibleEntryIds()[position.row.index],
          fieldId: this.#adapter.getVisibleFieldIds()[position.col.index],
        };
      case 'row':
        return {
          entryId: this.#adapter.getVisibleEntryIds()[position.row.index],
          fieldId: undefined,
        };
      case 'column':
        return {
          entryId: undefined,
          fieldId: this.#adapter.getVisibleFieldIds()[position.col.index],
        };
    }
  }

  /**
   * Selects specific fields (columns) by their IDs.
   * Converts field IDs to indices (view-aware) and dispatches to the selection machine.
   * Handles grouping contiguous columns into ranges:
   *
   * ['f1', 'f2', 'f3', 'f5'] -> [1, 2, 3, 5] -> [{anchor:1, lead:3}, {anchor:5, lead:5}]
   */
  selectFields(fieldIds: string[]): void {
    const visibleFieldIds = this.#adapter.getVisibleFieldIds();

    // Create Lookup Map (O(TotalCols))
    const idMap = new Map<string, number>();

    visibleFieldIds.forEach((id, i) => idMap.set(id, i));

    // Map & Sort (O(SelectionSize))
    const indices = fieldIds
      .map((id) => idMap.get(id))
      .filter((i): i is number => i !== undefined)
      .sort((a, b) => a - b);

    if (indices.length === 0) {
      return;
    }

    // Group contiguous indices (Linear Scan)
    const groups = indices.reduce<{ start: number; end: number }[]>((acc, idx) => {
      const last = acc[acc.length - 1];

      // If contiguous, extend the previous range
      if (last && idx === last.end + 1) {
        last.end = idx;
      } else {
        // Otherwise, start a new range
        acc.push({ start: idx, end: idx });
      }

      return acc;
    }, []);

    this.#machine.dispatch({
      type: 'selectColumns',
      ranges: groups.map((group) => ({
        // Axis Flip: Row is locked to 0, Col is the variable
        anchor: { row: { index: 0 }, col: { index: group.start } },
        lead: { row: { index: 0 }, col: { index: group.end } },
      })),
      source: 'api',
      focusRow: { index: 0 },
    });
  }

  /**
   * Selects specific entries by their IDs.
   * Converts entry IDs to indices (view-aware) and dispatches to the selection machine.
   * Handles converting plain ids to ranges:
   *
   * ['a1', 'a2', 'a3', 'a5'] -> [1, 2, 3, 5] -> [{anchor:1, lead:3}, {anchor:5, lead:5}]
   */
  selectEntries(entryIds: string[]): void {
    const visibleEntryIds = this.#adapter.getVisibleEntryIds();

    // Create Map (O(Rows))
    const idMap = new Map<string, number>();

    visibleEntryIds.forEach((id, i) => idMap.set(id, i));

    // Map & Sort (O(Selection))
    const indices = entryIds
      .map((id) => idMap.get(id))
      .filter((i): i is number => i !== undefined)
      .sort((a, b) => a - b);

    if (indices.length === 0) {
      return;
    }

    // Group contiguous indices
    const groups = indices.reduce<{ start: number; end: number }[]>((acc, idx) => {
      const last = acc[acc.length - 1];

      // If contiguous (current is exactly 1 greater than last end), extend it.
      if (last && idx === last.end + 1) {
        last.end = idx;
      } else {
        // Otherwise, start a new range.
        acc.push({ start: idx, end: idx });
      }
      return acc;
    }, []);

    this.#machine.dispatch({
      type: 'selectRows',
      ranges: groups.map((group) => ({
        anchor: { row: { index: group.start }, col: { index: 0 } },
        lead: { row: { index: group.end }, col: { index: 0 } },
      })),
      source: 'api',
      focusCol: { index: 0 },
    });
  }

  /**
   * Resolves a grid position (row/col indices) to database IDs (entryId/fieldId).
   * Returns undefined for either ID if the index is out of bounds.
   *
   * This is an O(1) lookup operation that uses the visible IDs arrays from the adapter.
   *
   * @example
   * ```ts
   * const ids = api.getIdsAtPosition({ row: { index: 5 }, col: { index: 2 } });
   * // ids might be: { entryId: 'entry-abc123', fieldId: 'field-xyz789' }
   * ```
   */
  getValueIdsAtPosition(position: { row: { index: number }; col: { index: number } }): {
    entryId: string | undefined;
    fieldId: string | undefined;
  } {
    const visibleFieldIds = this.#adapter.getVisibleFieldIds();
    const visibleEntryIds = this.#adapter.getVisibleEntryIds();

    return {
      fieldId: visibleFieldIds[position.col.index],
      entryId: visibleEntryIds[position.row.index],
    };
  }

  /**
   * Selects specific values by their IDs.
   * Dispatches selectCell for a single value, selectCellRange for multiple.
   * Note: When multiple values are provided the method computes a bounding box
   * spanning the minimum and maximum row and column indices and dispatches a
   * selectCellRange. This selects ALL cells within that rectangular range
   * (including cells that were not explicitly provided). Any provided IDs that do
   * not correspond to visible entries or fields are ignored.
   *
   * [{ entryId: 'e0', fieldId: 'f1' }, { entryId: 'e2', fieldId: 'f1' }] -> selectCellRange(anchor: (0,1), lead: (2,1))
   */
  selectValues(valueIds: { entryId: string; fieldId: string }[]): void {
    if (valueIds.length === 0) {
      return;
    }

    // Create Lookup Maps: O(Rows + Cols)
    const visibleEntryIds = this.#adapter.getVisibleEntryIds();
    const visibleFieldIds = this.#adapter.getVisibleFieldIds();

    const entryMap = new Map<string, number>();
    const fieldMap = new Map<string, number>();

    visibleEntryIds.forEach((id, i) => entryMap.set(id, i));
    visibleFieldIds.forEach((id, i) => fieldMap.set(id, i));

    // Fast path: single value skips the bounding box loop entirely if we have just one
    if (valueIds.length === 1) {
      const row = entryMap.get(valueIds[0].entryId);
      const col = fieldMap.get(valueIds[0].fieldId);

      if (row === undefined || col === undefined) {
        return;
      }

      this.#machine.dispatch({
        type: 'selectCell',
        cell: { row: { index: row }, col: { index: col } },
        source: 'api',
      });

      return;
    }

    // Single pass to compute bounding box: O(Input)
    // Sentinels (Infinity/-Infinity) let us skip the first-iteration check;
    // if no valid value is found, minRow stays Infinity and we bail out below.
    // essentially, this resolves values to indices and computes the bounding box
    // of the range, sorted with anchor being the top-left and the lead being the
    // bottom right.
    let minRow = Infinity;
    let maxRow = -Infinity;
    let minCol = Infinity;
    let maxCol = -Infinity;

    for (const { entryId, fieldId } of valueIds) {
      const row = entryMap.get(entryId);
      const col = fieldMap.get(fieldId);

      if (row === undefined || col === undefined) {
        continue;
      }

      minRow = Math.min(minRow, row);
      maxRow = Math.max(maxRow, row);
      minCol = Math.min(minCol, col);
      maxCol = Math.max(maxCol, col);
    }

    // No valid values resolved — nothing to select
    if (minRow === Infinity) {
      return;
    }

    // If all values collapsed to the same cell, selectCell is sufficient
    if (minRow === maxRow && minCol === maxCol) {
      this.#machine.dispatch({
        type: 'selectCell',
        cell: { row: { index: minRow }, col: { index: minCol } },
        source: 'api',
      });

      return;
    }

    this.#machine.dispatch({
      type: 'selectCellRange',
      anchor: { row: { index: minRow }, col: { index: minCol } },
      lead: { row: { index: maxRow }, col: { index: maxCol } },
      source: 'api',
    });
  }

  /**
   * Checks if a specific value (cell at entryId × fieldId) is currently selected.
   * Works across all selection modes: cell, row, column, and idle.
   * * Optimization Level:
   * - Cell Mode: O(1) - distinct fast path, no loops.
   * - Row/Col Mode: O(R) - loops only over range definitions, not cells.
   *
   * @param entryId - The database entry ID (row identifier)
   * @param fieldId - The database field ID (column identifier)
   * @returns `true` if the value is selected, `false` otherwise
   *
   * @example
   * Given a database like this (✓ = selected):
   *
   * ```
   *           field-a  field-b  field-c
   * entry-1      A        B        C
   * entry-2      D       ✓E        F
   * entry-3      G        H        I
   * ```
   *
   * **Cell mode** - Single cell (E) selected:
   * ```typescript
   * api.isValueSelected('entry-2', 'field-b') // true  ✓
   * api.isValueSelected('entry-2', 'field-a') // false
   * api.isValueSelected('entry-1', 'field-b') // false
   * ```
   *
   * **Row mode** - Entire row 2 selected:
   * ```
   *           field-a  field-b  field-c
   * entry-1      A        B        C
   * entry-2     ✓D       ✓E       ✓F
   * entry-3      G        H        I
   * ```
   * ```typescript
   * api.isValueSelected('entry-2', 'field-a') // true  ✓
   * api.isValueSelected('entry-2', 'field-b') // true  ✓
   * api.isValueSelected('entry-2', 'field-c') // true  ✓
   * api.isValueSelected('entry-1', 'field-a') // false
   * ```
   *
   * **Column mode** - Entire column B selected:
   * ```
   *           field-a  field-b  field-c
   * entry-1      A       ✓B        C
   * entry-2      D       ✓E        F
   * entry-3      G       ✓H        I
   * ```
   * ```typescript
   * api.isValueSelected('entry-1', 'field-b') // true  ✓
   * api.isValueSelected('entry-2', 'field-b') // true  ✓
   * api.isValueSelected('entry-3', 'field-b') // true  ✓
   * api.isValueSelected('entry-1', 'field-a') // false
   * ```
   *
   * **Idle mode** - Nothing selected:
   * ```typescript
   * api.isValueSelected('entry-1', 'field-a') // false
   * api.isValueSelected('entry-2', 'field-b') // false
   * ```
   *
   * **Performance**:
   * Zero allocation. Complexity is dominated by finding the indices of the
   * provided IDs within the visible range. The check against the selection
   * itself is effectively instant, regardless of selection size.
   */
  isValueSelected(entryId: string, fieldId: string): boolean {
    const state = this.#machine.getState();

    // Quick exit if nothing is selected
    if (state.mode === 'idle' || state.selections.length === 0) {
      return false;
    }

    // Resolve IDs to View Indices
    // (We still need to do this to know "where" the ID is visually)
    const visibleEntryIds = this.#adapter.getVisibleEntryIds();
    const visibleFieldIds = this.#adapter.getVisibleFieldIds();

    const rowIndex = visibleEntryIds.indexOf(entryId);
    const colIndex = visibleFieldIds.indexOf(fieldId);

    // If ID is not in the current view, it cannot be selected
    if (rowIndex === -1 || colIndex === -1) {
      return false;
    }

    if (state.mode === 'cell') {
      // Guaranteed to be the only one
      const selection = state.selections[0];

      // Normalize range (handle dragging backwards)
      const minRow = Math.min(selection.anchor.row.index, selection.lead.row.index);
      const maxRow = Math.max(selection.anchor.row.index, selection.lead.row.index);
      const minCol = Math.min(selection.anchor.col.index, selection.lead.col.index);
      const maxCol = Math.max(selection.anchor.col.index, selection.lead.col.index);

      return (
        rowIndex >= minRow &&
        rowIndex <= maxRow &&
        colIndex >= minCol &&
        colIndex <= maxCol
      );
    }

    // ROW/COLUMN MODE: Loop Path (Multiple Ranges Possible)
    // We iterate the ranges (e.g., 3 ranges), not the cells (e.g., 10,000 cells).
    for (const range of state.selections) {
      const { anchor, lead } = range;

      if (state.mode === 'row') {
        const min = Math.min(anchor.row.index, lead.row.index);
        const max = Math.max(anchor.row.index, lead.row.index);

        if (rowIndex >= min && rowIndex <= max) {
          return true;
        }
      } else if (state.mode === 'column') {
        const min = Math.min(anchor.col.index, lead.col.index);
        const max = Math.max(anchor.col.index, lead.col.index);

        if (colIndex >= min && colIndex <= max) {
          return true;
        }
      }
    }

    return false;
  }

  #resolveColumnSelection(columnIndices: number[]): ResolvedSelection {
    const fieldIds = this.#adapter.getVisibleFieldIds();
    const fields = columnIndices
      .map((col) => {
        const fieldId = fieldIds[col];

        if (!fieldId) {
          return undefined;
        }

        // if we have a fieldId, look it up in the adapter
        const field = this.#adapter.getFieldById(fieldId);

        if (!field) {
          return undefined;
        }

        return { fieldId, field };
      })
      .filter((f) => f !== undefined);

    return { mode: 'column', fields };
  }

  #resolveRowSelection(rowValues: number[] | 'all'): ResolvedSelection {
    const entryIds = this.#adapter.getVisibleEntryIds();

    const indicesToResolve = rowValues === 'all' ? entryIds.map((_, i) => i) : rowValues;

    const entries = indicesToResolve
      .map((row) => {
        const entryId = entryIds[row];

        if (!entryId) {
          return undefined;
        }

        const entry = this.#adapter.getEntryById(entryId);

        if (!entry) {
          return undefined;
        }

        return { entryId, entry };
      })
      .filter((e) => e !== undefined);

    return { mode: 'row', entries };
  }

  #resolveCellSelection(cells: { row: number; col: number }[]): ResolvedSelection {
    const fieldIds = this.#adapter.getVisibleFieldIds();
    const entryIds = this.#adapter.getVisibleEntryIds();

    // Per-call caches to avoid redundant adapter lookups during drag selections.
    // For a 10x10 cell selection (100 cells), this reduces adapter calls from
    // 200 (2 per cell) to ~20 (10 unique fields + 10 unique entries).
    const fieldCache = new Map<string, ReturnType<DatabaseAdapter['getFieldById']>>();
    const entryCache = new Map<string, ReturnType<DatabaseAdapter['getEntryById']>>();

    const values = cells
      .map((cell) => {
        const fieldId = fieldIds[cell.col];
        const entryId = entryIds[cell.row];

        if (!fieldId || !entryId) {
          return undefined;
        }

        // Cache lookup with explicit undefined handling: Map.get() returns undefined
        // both when key is missing AND when value is undefined. We use .has() to
        // distinguish "not yet fetched" from "fetched but field doesn't exist".
        let field = fieldCache.get(fieldId);

        if (field === undefined && !fieldCache.has(fieldId)) {
          field = this.#adapter.getFieldById(fieldId);
          fieldCache.set(fieldId, field);
        }

        let entry = entryCache.get(entryId);

        if (entry === undefined && !entryCache.has(entryId)) {
          entry = this.#adapter.getEntryById(entryId);
          entryCache.set(entryId, entry);
        }

        if (!field || !entry) {
          return undefined;
        }

        const value = this.#getValueFromEntry(entry, fieldId);

        return { fieldId, entryId, field, entry, value };
      })
      .filter((v) => v !== undefined);

    return { mode: 'cell', values };
  }

  #getValueFromEntry(entry: DatabaseEntry, fieldId: string): DatabaseValue | undefined {
    return entry.values[fieldId];
  }

  /**
   * Computes the bounding box of the current selection.
   * Returns undefined for idle mode or empty selections.
   *
   * * Optimization Level:
   * - Cell Mode: O(1) - returns the single range's bounds directly.
   */
  #getSelectionBounds():
    | { minRow: number; maxRow: number; minCol: number; maxCol: number }
    | undefined {
    const state = this.#machine.getState();

    if (state.mode === 'idle' || state.selections.length === 0) {
      return undefined;
    }

    if (state.mode === 'cell') {
      // we guarantee that there's only ONE selection when in cell mode
      const { anchor, lead } = state.selections[0];

      return {
        minRow: Math.min(anchor.row.index, lead.row.index),
        maxRow: Math.max(anchor.row.index, lead.row.index),
        minCol: Math.min(anchor.col.index, lead.col.index),
        maxCol: Math.max(anchor.col.index, lead.col.index),
      };
    }

    // ROW / COLUMN MODE: Aggregate Bounds
    // We need to find the "super-box" that contains all disjoint selections.
    let globalMinRow = Infinity;
    let globalMaxRow = -Infinity;
    let globalMinCol = Infinity;
    let globalMaxCol = -Infinity;

    for (const range of state.selections) {
      const r1 = range.anchor.row.index;
      const r2 = range.lead.row.index;
      const c1 = range.anchor.col.index;
      const c2 = range.lead.col.index;

      // Expand global bounds
      globalMinRow = Math.min(globalMinRow, r1, r2);
      globalMaxRow = Math.max(globalMaxRow, r1, r2);
      globalMinCol = Math.min(globalMinCol, c1, c2);
      globalMaxCol = Math.max(globalMaxCol, c1, c2);
    }

    const totalEntries = this.#adapter.getVisibleEntryIds().length;
    const totalFields = this.#adapter.getVisibleFieldIds().length;

    if (state.mode === 'row') {
      return {
        minRow: globalMinRow,
        maxRow: globalMaxRow,
        minCol: 0,
        maxCol: totalFields - 1,
      };
    }

    if (state.mode === 'column') {
      return {
        minRow: 0,
        maxRow: totalEntries - 1,
        minCol: globalMinCol,
        maxCol: globalMaxCol,
      };
    }

    return undefined;
  }
}
